import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Tag, Select, Btn, EmptyState } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { loadStaffBookings, loadRefData } from "@/lib/staffData";
import { updateBucket, formatReg, fmtDateTime, REQUEST_LABEL } from "@/lib/vda";

async function loadExceptions() {
  const { data } = await supabase
    .from("allocation_exceptions")
    .select("*, drivers(full_name), vehicles(registration), customers(name)")
    .eq("status", "open")
    .order("created_at", { ascending: false });
  return data || [];
}

export default function Oversight() {
  const { session, profile } = useAuth();
  const uid = session?.user?.id;
  const { data, loading, reload } = useAsync(async () => ({
    bookings: await loadStaffBookings(), ref: await loadRefData(), exceptions: await loadExceptions(),
  }), []);
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const stats = useMemo(() => {
    const b = data?.bookings || [];
    const open = b.filter((x) => x.internal_status !== "completed");
    const byCustomer = {};
    (data?.ref.customers || []).forEach((c) => { byCustomer[c.id] = { name: c.name, open: 0, overdue: 0, ready: 0 }; });
    open.forEach((x) => {
      const c = byCustomer[x.customer_id]; if (!c) return;
      c.open++;
      if (updateBucket(x.next_update_due_at).key === "overdue") c.overdue++;
      if (x.internal_status === "vehicle_complete") c.ready++;
    });
    const byAE = {};
    open.forEach((x) => {
      const key = x.assignee?.full_name || "Unassigned";
      byAE[key] = (byAE[key] || 0) + 1;
    });
    return { customers: Object.values(byCustomer), byAE, unassigned: open.filter((x) => !x.assigned_staff_user_id) };
  }, [data]);

  if (loading) return <StaffShell><Spinner /></StaffShell>;

  const reassign = async (bookingId, staffId, booking) => {
    setBusy(true);
    try {
      await supabase.from("booking_requests").update({ assigned_staff_user_id: staffId }).eq("id", bookingId);
      const name = data.ref.staff.find((s) => s.id === staffId)?.full_name || "AE";
      await supabase.from("booking_updates").insert({
        booking_request_id: bookingId, update_type: "assignment", previous_internal_status: booking.internal_status,
        internal_note: `Reassigned to ${name} by ${profile.full_name}.`, created_by_user_id: uid, source: "individual",
      });
      toast.success("Booking reassigned.");
      reload();
    } catch (e) { toast.error(e.message || "Could not reassign"); }
    finally { setBusy(false); }
  };

  const resolveException = async (ex) => {
    setBusy(true);
    try {
      await supabase.from("allocation_exceptions").update({
        status: "resolved", reviewed_by_user_id: uid, resolved_at: new Date().toISOString(),
        resolution_note: "Reviewed by oversight.",
      }).eq("id", ex.id);
      await supabase.from("audit_events").insert({
        entity_type: "allocation_exception", entity_id: ex.id, action: "exception_resolved", actor_user_id: uid,
      });
      toast.success("Exception resolved.");
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Oversight</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-5">Customer &amp; performance overview</h1>

      <h2 className="font-semibold mb-2">Customer summaries</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.customers.map((c) => (
          <Panel key={c.name} className="p-4" data-testid={`oversight-customer-${c.name}`}>
            <div className="font-medium">{c.name}</div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-xl font-semibold text-primary">{c.open}</div><div className="vda-tick text-muted-foreground">Open</div></div>
              <div><div className="text-xl font-semibold text-[hsl(var(--vda-red))]">{c.overdue}</div><div className="vda-tick text-muted-foreground">Overdue</div></div>
              <div><div className="text-xl font-semibold text-[hsl(var(--vda-amber))]">{c.ready}</div><div className="vda-tick text-muted-foreground">Ready</div></div>
            </div>
          </Panel>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <Panel className="p-5">
          <h2 className="font-semibold mb-3">Workload by Account Executive</h2>
          <div className="space-y-2">
            {Object.entries(stats.byAE).map(([name, n]) => (
              <div key={name} className="flex items-center justify-between text-sm">
                <span className={name === "Unassigned" ? "text-[hsl(var(--vda-clay))]" : ""}>{name}</span>
                <Tag tone="muted">{n} open</Tag>
              </div>
            ))}
          </div>
        </Panel>

        <Panel className="p-5">
          <h2 className="font-semibold mb-3">Allocation exceptions</h2>
          {data.exceptions.length === 0 ? <p className="text-sm text-muted-foreground">No open exceptions.</p> : (
            <div className="space-y-2">
              {data.exceptions.map((ex) => (
                <div key={ex.id} className="border border-border rounded-[3px] p-3" data-testid={`exception-${ex.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="vda-reg">{formatReg(ex.vehicles?.registration)}</span> · {ex.drivers?.full_name} · {ex.customers?.name}
                    </div>
                    <Btn variant="outline" className="h-8 px-3" data-testid={`resolve-${ex.id}`} onClick={() => resolveException(ex)} disabled={busy}>Resolve</Btn>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{ex.exception_type.replace(/_/g, " ")} · {fmtDateTime(ex.created_at)}</div>
                  {ex.driver_message && <p className="text-sm mt-1">{ex.driver_message}</p>}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <h2 className="font-semibold mt-6 mb-2">Reassign work {stats.unassigned.length > 0 && <Tag tone="clay">{stats.unassigned.length} unassigned</Tag>}</h2>
      {(data.bookings.filter((b) => b.internal_status !== "completed")).length === 0 ? <EmptyState title="No open bookings" /> : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Reference</th>
                  <th className="px-3 py-2 font-medium">Reg</th>
                  <th className="px-3 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Assign AE</th>
                </tr>
              </thead>
              <tbody>
                {data.bookings.filter((b) => b.internal_status !== "completed").map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="px-3 py-2 vda-reg cursor-pointer hover:underline" onClick={() => nav(`/staff/booking/${b.id}`)}>{b.human_reference}</td>
                    <td className="px-3 py-2 vda-reg">{formatReg(b.vehicles?.registration)}</td>
                    <td className="px-3 py-2">{b.customers?.name}</td>
                    <td className="px-3 py-2">{REQUEST_LABEL[b.request_type] || b.request_type}</td>
                    <td className="px-3 py-2">
                      <Select value={b.assigned_staff_user_id || ""} onChange={(e) => reassign(b.id, e.target.value, b)} disabled={busy} data-testid={`reassign-${b.human_reference}`} className="h-9 w-auto min-w-[160px]">
                        <option value="">Unassigned</option>
                        {data.ref.staff.filter((s) => s.role === "account_executive" || s.role === "senior_account_executive").map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </StaffShell>
  );
}
